import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useAuthStore } from "../../store/authStore";
import { useLanguage } from "../../contexts/LanguageContext";
import LegalLinks from "../../legal/LegalLinks";

const Root = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;
const Backdrop = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 767px) {
    display: ${({ $open }) => ($open ? "block" : "none")};
    position: fixed;
    inset: 0;
    z-index: ${({ theme }) => theme.zIndex.fixed - 5};
    background: #05070ab3;
  }
`;
const Main = styled.div`
  min-width: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  margin-left: ${({ theme }) => theme.layout.sidebar};
  @media (min-width: 768px) and (max-width: 1279px) {
    margin-left: ${({ theme }) => theme.layout.rail};
  }
  @media (max-width: 767px) {
    margin-left: 0;
  }
`;
const Content = styled.main`
  flex: 1;
  width: 100%;
  max-width: calc(${({ theme }) => theme.layout.content} + 2 * 32px);
  margin: 0 auto;
  padding: 8px clamp(16px, 2.5vw, 32px) 48px;
  min-width: 0;
  &:focus {
    outline: none;
  }
  @media (max-width: 767px) {
    padding: 8px 16px 32px;
  }
`;

const FootWrap = styled.div`
  width: 100%;
  max-width: calc(${({ theme }) => theme.layout.content} + 2 * 32px);
  margin: 0 auto;
  padding: 0 clamp(16px, 2.5vw, 32px);
`;

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    toggleRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  return (
    <Root>
      <a className="skip-link" href="#main-content">
        {t("common.skip_to_content")}
      </a>
      <Backdrop $open={open} onClick={close} />
      <Sidebar isOpen={open} onClose={() => setOpen(false)} />
      <Main>
        <Header user={user} menuButtonRef={toggleRef} onToggleSidebar={() => setOpen((v) => !v)} />
        <Content id="main-content" tabIndex={-1}>
          {children}
        </Content>
        <FootWrap>
          <LegalLinks />
        </FootWrap>
      </Main>
    </Root>
  );
}
